import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { ImageEditDialog } from '@/components/tiptap-editor/tiptap-extension/image-edit-dialog';
import { ImageEditTooltip } from '@/components/tiptap-editor/tiptap-extension/image-edit-tooltip';
import { ImageHoverAltText } from '@/components/tiptap-editor/tiptap-extension/image-hover-alttext';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import type { ReactNodeViewProps } from '@tiptap/react';
import { memo, useState } from 'react';

import {
    convertImageNodeType,
    normalizeImportedImageSrc,
    useImageDragSurface,
    useImageResizableMount,
} from '@/components/tiptap-editor/tiptap-extension/image-utils';
import { syncMaImageAltText } from '@/lib/ma-image-alt-text';

function ZyImageResizableMount({
    containerRef,
    editor,
    getPos,
}: {
    containerRef: React.RefObject<HTMLElement | null>;
    editor: ReactNodeViewProps['editor'];
    getPos: ReactNodeViewProps['getPos'];
}) {
    useImageDragSurface({ containerRef, editor, getPos, nodeTypeName: 'image' });

    return (
        <div
            ref={containerRef as any}
            className="block w-fit max-w-full"
        />
    );
}

const ZyImageNodeView: React.FC<ReactNodeViewProps> = ({
    node,
    selected,
    updateAttributes,
    editor,
    getPos,
    decorations,
    innerDecorations,
}) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { attrs } = node;
    const normalizedSrc = normalizeImportedImageSrc(attrs, editor.state?.doc?.attrs);
    const hasAlt = !!attrs.alt;
    const { containerRef, isResizing } = useImageResizableMount({
        editor,
        node,
        getPos,
        nodeTypeName: 'image',
        decorations,
        innerDecorations,
    });
    const [isOpen, setIsOpen] = useState(false);
    const [alt, setAlt] = useState(attrs.alt);
    const [height, setHeight] = useState(String(attrs.height ?? ''));
    const [width, setWidth] = useState(String(attrs.width ?? ''));
    const [pull, setPull] = useState(attrs.pull);
    const [displayType, setDisplayType] = useState<'block' | 'inline'>('block');
    const [isHovering, setIsHovering] = useState(false);

    const refreshImageProperties = () => {
        const { alt, height, width, pull } = attrs;
        setAlt(alt);
        setHeight(String(height ?? ''));
        setWidth(String(width ?? ''));
        setPull(pull);
        setDisplayType('block');
    };

    const parsedWidth = Number(width);
    const parsedHeight = Number(height);

    const getPullClass = () => {
        switch (attrs.pull) {
            case 'left':
                return 'justify-start';
            case 'right':
                return 'justify-end';
            default:
                return 'justify-center';
        }
    };

    const saveImage = () => {
        const trimmedAlt = alt?.trim() ?? '';
        const nextAttrs = {
            ...attrs,
            alt: trimmedAlt,
            width: parsedWidth,
            height: parsedHeight,
            aspectRatio: parsedWidth / parsedHeight,
            pull,
        };

        if (displayType === 'inline') {
            convertImageNodeType(editor, getPos, 'inline', nextAttrs);
            syncMaImageAltText(attrs.maImageId, trimmedAlt);
            setIsOpen(false);
            return;
        }

        updateAttributes(nextAttrs);
        syncMaImageAltText(attrs.maImageId, trimmedAlt);
        setIsOpen(false);
    };

    return (
        <ZyNodeViewWrapper
            decorations={decorations}
            label="Image"
            selected={selected}
            className={`flex items-center ${getPullClass()}`}
        >
            <div
                className="relative block w-fit max-w-full"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onDoubleClick={() => {
                    if (isEditorReadonly) {
                        return;
                    }
                    // refreshImageProperties is required so the modal can contain the current
                    // height and width of the photo. On first render, attrs.height/width are null,
                    // and this func allows us to retrieve them once they are loaded.
                    refreshImageProperties();
                    setIsOpen(true);
                }}
            >
                <ZyImageResizableMount
                    containerRef={containerRef}
                    editor={editor}
                    getPos={getPos}
                />

                <ImageEditTooltip
                    isHovering={isHovering}
                    isResizing={isResizing}
                    hasAlt={hasAlt}
                    width={attrs.width}
                    refreshImageProperties={refreshImageProperties}
                    setIsOpen={setIsOpen}
                    editDisabled={isEditorReadonly}
                />

                <ImageHoverAltText
                    isResizing={isResizing}
                    isHovering={isHovering}
                    alt={attrs.alt}
                />
            </div>
            <ImageEditDialog
                open={isOpen}
                onOpenChange={open => {
                    if (open && isEditorReadonly) {
                        return;
                    }
                    if (open) {
                        refreshImageProperties();
                    }
                    setIsOpen(open);
                }}
                maImageId={attrs.maImageId}
                currentMaDocumentId={editor.state.doc.attrs.documentId}
                previewSrc={normalizedSrc}
                previewAlt={attrs.alt}
                previewWidth={attrs.width}
                previewHeight={attrs.height}
                alt={alt}
                setAlt={setAlt}
                aspectRatio={attrs.aspectRatio}
                height={height}
                setHeight={setHeight}
                width={width}
                setWidth={setWidth}
                displayType={displayType}
                onDisplayTypeChange={setDisplayType}
                pull={pull}
                onPullChange={setPull}
                onCancel={() => setIsOpen(false)}
                onSave={saveImage}
            />
        </ZyNodeViewWrapper>
    );
};

export default memo(ZyImageNodeView);
